// 1. React imports
import { useState } from 'react';

// 2. External libraries

// 3. Internal components
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { Badge } from '~/components/ui/badge';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '~/components/ui/field';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { WizardActions } from '~/components/onboarding/WizardActions';

// 4. Hooks and utilities
import {
  useCreateConnectionMutation,
  useRotateConnectionKeyMutation,
} from '~/hooks/useConnectionMutations';

/**
 * Client type selection for connection creation.
 */
type ClientType = 'mcp' | 'rest';

/**
 * Props for SetupClientStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface SetupClientStepProps {
  topicId: string;
  topicName: string;
  onConnectionCreated: (connectionId: string, clientType: ClientType, apiKey: string) => void;
  onBack: () => void;
  onSkip: () => void;
  existingConnectionId?: string | null;
  existingClientType?: 'mcp' | 'rest' | null;
  existingApiKey?: string | null;
}

/**
 * Setup client step for onboarding wizard.
 *
 * Displays MCP and REST API options as a single-tab-stop radio group, each
 * option's details shown inline via FieldDescription. Creates a connection
 * via API and passes the result to the parent callback.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.5.1: useCreateConnectionMutation with full type parameters.
 * Layout per spec: Header → Client Type Field → Error → Actions.
 *
 * @example
 * ```tsx
 * <SetupClientStep
 *   topicId="topic-123"
 *   topicName="Agent Messages"
 *   onConnectionCreated={(id, type, key) => console.log(id, type, key)}
 *   onBack={() => setStep(2)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function SetupClientStep({
  topicId,
  topicName,
  onConnectionCreated,
  onBack,
  onSkip,
  existingConnectionId = null,
  existingClientType = null,
  // Note: existingApiKey intentionally omitted - we always rotate to get a fresh key
}: SetupClientStepProps) {
  // Per §TS.3.3: Explicit type parameters for useState
  // Initialize from existingClientType to prevent duplicate connection creation
  const [selectedClientType, setSelectedClientType] = useState<ClientType>(
    existingClientType ?? 'mcp'
  );
  const [error, setError] = useState<string | null>(null);

  // Per §TS.5.1: useCreateConnectionMutation provides full type safety
  const createConnectionMutation = useCreateConnectionMutation(topicId);
  // Rotate key mutation - only valid when existingConnectionId is present
  const rotateKeyMutation = useRotateConnectionKeyMutation(topicId, existingConnectionId ?? '');

  // Event handler with handle prefix
  const handleClientTypeChange = (clientType: ClientType) => {
    setSelectedClientType(clientType);
    setError(null);
  };

  // Event handler with handle prefix
  const handleNext = async () => {
    setError(null);

    // If we have an existing connection, rotate its key to get a fresh API key
    // This handles returning users whose apiKey may be null in frontend state
    if (existingConnectionId) {
      try {
        const response = await rotateKeyMutation.mutateAsync();
        if (!response.apiKey) {
          setError('Failed to rotate connection key: no API key returned');
          return;
        }
        const typeToUse = existingClientType ?? selectedClientType;
        onConnectionCreated(existingConnectionId, typeToUse, response.apiKey);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to rotate connection key';
        setError(errorMessage);
      }
      return;
    }

    // No existing connection - create a new one
    try {
      const response = await createConnectionMutation.mutateAsync({
        type: selectedClientType,
        name: selectedClientType === 'mcp' ? 'My MCP Client' : 'REST API Client',
      });

      if (!response.id || !response.apiKey) {
        setError('Connection created but missing required fields');
        return;
      }

      onConnectionCreated(response.id, selectedClientType, response.apiKey);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create connection';
      setError(errorMessage);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <FieldSet>
        <FieldLegend>Choose your client</FieldLegend>
        <FieldDescription>
          Choose how you read from {topicName}. MCP is recommended if you are already using an LLM
          client that supports it.
        </FieldDescription>

        {/* Client type selection: each option is a single tab stop (the radio),
            with its details always visible via FieldDescription instead of a
            separate accordion trigger. */}
        <RadioGroup
          value={selectedClientType}
          onValueChange={(value) => handleClientTypeChange(value as ClientType)}
        >
          {/* MCP Client - Primary Option */}
          <FieldLabel htmlFor="client-type-mcp">
            <Field orientation="horizontal">
              <RadioGroupItem value="mcp" id="client-type-mcp" />
              <FieldContent>
                <FieldTitle>
                  MCP Client
                  <Badge>Recommended</Badge>
                </FieldTitle>
                <FieldDescription>
                  Model Context Protocol provides seamless integration with AI assistants. Configure
                  once and use across all your tools.
                </FieldDescription>
              </FieldContent>
            </Field>
          </FieldLabel>

          {/* REST API - Secondary Option */}
          <FieldLabel htmlFor="client-type-rest">
            <Field orientation="horizontal">
              <RadioGroupItem value="rest" id="client-type-rest" />
              <FieldContent>
                <FieldTitle>REST API</FieldTitle>
                <FieldDescription>
                  Direct HTTP API access for custom integrations. Use standard REST endpoints to
                  send and receive messages programmatically.
                </FieldDescription>
              </FieldContent>
            </Field>
          </FieldLabel>
        </RadioGroup>
      </FieldSet>

      {/* Error Alert - Conditional */}
      {error && <ErrorAlert title={null} message={error} />}

      {/* Wizard Actions */}
      <WizardActions
        onBack={onBack}
        onNext={() => void handleNext()}
        onSkip={onSkip}
        nextLabel="Next"
        showBack={true}
        isLoading={createConnectionMutation.isPending || rotateKeyMutation.isPending}
      />
    </div>
  );
}
